import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { Layer } from 'bernie-view';
import { QueryRoute, updateSearch } from 'bernie-core';

import { FormInputs, FormInputsProvider, FormQueryVariables, useFetchRatesAction } from '@shared-ui/lodging-form';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { SearchCriteriaModel } from 'stores/models';

import { UitkDatePickerProps, UitkNewDatePicker } from 'uitk-react-date-picker';
import { UitkLink } from 'uitk-react-link';

import { useFormInputs } from 'components/infosite/shared-ui/form/use-form-inputs';

interface DateFieldProps {
  rfrr?: string;
  onChange?: (event: any, isTriggeredByStepper?: boolean) => void;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  context?: ContextStore;
}

export type CustomTriggerDateFieldProps = DateFieldProps & UitkDatePickerProps;

const isValidDate = (date?: Date | undefined): boolean => Boolean(date && date.toDateString() !== 'Invalid Date');

const DateField: React.FC<CustomTriggerDateFieldProps> = inject(
  'analytics',
  'currentHotel'
)(props => {
  const { buttonText, analytics, rfrr, triggeredSelector, currentHotel } = props;
  const { formatDateString, formatDate, getWeekData } = useLocalization();
  const history = useHistory();
  const location = useLocation();

  const offerSearchData = currentHotel?.offerSearchData;

  const startDate = offerSearchData?.startDate;
  const endDate = offerSearchData?.endDate;
  const startDateFormatted = startDate && formatDateString(startDate, { skeleton: 'MMMEd' });
  const endDateFormatted = endDate && formatDateString(endDate, { skeleton: 'MMMEd' });
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  const checkInDate = startDate ? new Date(startDate) : undefined;
  const checkOutDate = endDate ? new Date(endDate) : undefined;

  const [rates, disclaimer, fetchRates] = useFetchRatesAction(isValidDate(checkInDate) && isValidDate(checkOutDate));

  const open = () => {
    setShowDatePicker(true);
    if (checkInDate && isValidDate(checkInDate)) {
      fetchRates(checkInDate);
    }
    updateSearch({ history, location, newParams: { pwaDialog: `datepicker-${triggeredSelector}` } });

    if (typeof analytics?.trackEvent === 'function' && rfrr) {
      analytics.trackEvent(rfrr, 'Open Date Picker', analytics.omnitureData);
    }
  };

  const save = (start: Date, end: Date) => {
    props.onChange &&
      props.onChange({
        target: [
          {
            name: 'startDate',
            value: formatDate(start, { raw: 'yyyy-MM-dd' }),
          },
          {
            name: 'endDate',
            value: formatDate(end, { raw: 'yyyy-MM-dd' }),
          },
        ],
      });
  };

  const dismiss = () => {
    setShowDatePicker(false);
    history.goBack();
  };

  const datePickerAnalytics = {
    closeAnalytics: analytics?.setupTracker(':ChangeDatesClose', 'Close Date Picker', null),
    doneAnalytics: analytics?.setupTracker(':ChangeDatesDone', 'Apply Date Picker', null),
  };

  const routerLinkWrapper = (children: HTMLElement) => <>{children}</>;
  const firstDayOfWeek: number = parseInt(getWeekData().firstDay.toUpperCase(), 10); // No mount callback

  const dateFieldProps = {
    routerLinkWrapper,
    showDatePicker,
    useCustomTrigger: true,
  };

  const datePickerProps = {
    analytics: datePickerAnalytics,
    dateFieldProps,
    datePickerContent: rates,
    footerText: disclaimer,

    onDismissCB: dismiss,
    onSubmitCB: save,

    buttonText,
    startDate: startDate ?? '',
    endDate,
    doubleMonths: true,
    daysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
    firstDayOfWeek,
    midnightBookingOffset: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
    triggeredSelector,
  };

  return (
    <>
      <UitkLink align="center">
        <button onClick={open}>{triggeredSelector === 'start' ? startDateFormatted : endDateFormatted}</button>
      </UitkLink>
      {showDatePicker && (
        <QueryRoute query={{ pwaDialog: `datepicker-${triggeredSelector}` }}>
          <Layer id={`home-datepicker-${triggeredSelector}`} onDismiss={dismiss}>
            <UitkNewDatePicker {...datePickerProps} />
          </Layer>
        </QueryRoute>
      )}
    </>
  );
});

DateField.defaultProps = {
  startDate: '',
  endDate: '',
};

export const CustomTriggerDateFieldWrapper: React.FC<CustomTriggerDateFieldProps> = inject('currentHotel')(props => {
  const { currentHotel } = props;
  const { context } = useBexApi();
  const hotelId = currentHotel?.hotelId;
  const searchCriteria = currentHotel?.searchCriteria;
  const inputs = hotelId && searchCriteria && useFormInputs(hotelId, searchCriteria);

  const variable: FormQueryVariables = {
    ...((inputs || {}) as FormInputs),
    context,
  };

  if (!hotelId || !searchCriteria) {
    return null;
  }

  return (
    <FormInputsProvider value={variable}>
      <DateField {...props} />
    </FormInputsProvider>
  );
});

CustomTriggerDateFieldWrapper.displayName = 'CustomTriggerDateFieldWrapper';
